/**
 * Autorização efetiva do CRM (fonte única no servidor: get_my_crm_access).
 * Acesso = rede com direito vigente (assinatura/teste) + usuário com permissão.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSessionContext } from '@/context/SessionContextProvider';

export type CrmAccessState =
  | 'loading'
  | 'allowed'
  | 'no_permission'
  | 'trial_expired'
  | 'suspended'
  | 'not_contracted'
  | 'error';

export function useCrmAccess() {
  const { networkId } = useSessionContext();

  const query = useQuery({
    queryKey: ['crm-access', networkId],
    enabled: !!networkId,
    queryFn: async (): Promise<CrmAccessState> => {
      const { data, error } = await supabase.rpc('get_my_crm_access' as any, { _network_id: networkId } as any);
      if (error) throw error;
      const state = (data as any)?.state as CrmAccessState | undefined;
      return state ?? 'no_permission';
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: 'always',
    retry: 2,
  });

  const state: CrmAccessState = !networkId || query.isLoading
    ? 'loading'
    : query.isError
      ? 'error'
      : (query.data ?? 'loading');

  return { state, isAllowed: state === 'allowed', refetch: query.refetch };
}
